import { useState } from "react";
import Editor from "@monaco-editor/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui-kit/dialog";
import { Button } from "@/components/ui-kit/button";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { useTheme } from "@/styles/theme/theme-provider";
import { Alert, AlertDescription } from "@/components/ui-kit/alert";

import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AgentBehaviorSettingsForm } from "@/modules/gpt-chats/components/agents-configuration/components/config-card-section/utils";

interface JsonEditorModalProps {
  form: UseFormReturn<AgentBehaviorSettingsForm>;
  onClose: () => void;
  onSave: (jsonSchema: string) => Promise<void>;
  isMobile: boolean;
}

export const JsonEditorModal = ({ form, onClose, onSave }: JsonEditorModalProps) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const initialValue = form.getValues("response_format") || "{}";
  const [jsonValue, setJsonValue] = useState(initialValue);
  const [error, setError] = useState<string>("");
  const [isValid, setIsValid] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const validateJson = (value: string) => {
    try {
      JSON.parse(value);
      setError("");
      setIsValid(true);
      return true;
    } catch (e: any) {
      setError(e.message);
      setIsValid(false);
      return false;
    }
  };

  const handleEditorChange = (value: string | undefined) => {
    const newValue = value || "{}";
    setJsonValue(newValue);
    validateJson(newValue);
  };

  const handleSave = async () => {
    if (!validateJson(jsonValue)) {
      return;
    }

    if (jsonValue.trim() === "{}" || jsonValue.trim() === "") {
      setError(t('PROVIDE_VALID_JSON_SCHEMA'));
      setIsValid(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(jsonValue);
    } catch (error) {
      console.error("Failed to save JSON schema:", error);
      setError(t('FAILED_TO_SAVE_JSON_SCHEMA'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="flex max-h-[80vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle>{t('JSON_RESPONSE_SCHEMA')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 flex-col gap-3 overflow-hidden">
          <div className="flex-1 overflow-hidden rounded-lg border">
            <Editor
              height="400px"
              defaultLanguage="json"
              value={jsonValue}
              onChange={handleEditorChange}
              theme={theme === "dark" ? "vs-dark" : "vs-light"}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: "on",
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 2,
              }}
            />
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isValid && !error && jsonValue !== "{}" && (
            <Alert className="bg-green-50 py-1 dark:bg-green-950">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription className="text-success">{t('VALID_JSON_SCHEMA')}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="flex justify-end">
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleSave} disabled={!isValid || isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('SAVING')}
                </>
              ) : (
                t('SAVE_SCHEMA')
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
